import { useEffect, useId, useRef, useState } from 'react';
import { ArrowClockwise } from '@phosphor-icons/react';
import { ScreenReaderLabel } from '../../components/common/ScreenReaderLabel';

const SPINNING_ICON_CLASS = 'animate-spin';

function recheckIconClass(active: boolean): string | undefined {
  if (!active) return undefined;
  return SPINNING_ICON_CLASS;
}

type RowRecheckButtonProps = {
  onRun: () => Promise<void>;
  label: string;
  /** Hover hint. Kept free of the row's handle: a native title is an attribute,
   *  and session recordings upload attributes unmasked (issue #727). */
  tooltip: string;
  /** Spin + disable while a re-check started earlier is still settling
   *  server-side (the request itself may already have returned). */
  pending?: boolean;
};

/** Compact per-row ⟳ action for admin re-checks (audio compliance, creator
 *  profile). Toasts are the mutation's job; this only tracks busy state. */
export function RowRecheckButton({ onRun, label, tooltip, pending = false }: RowRecheckButtonProps) {
  const [busy, setBusy] = useState(false);
  const labelId = useId();
  // The mutation's own invalidate() can refetch and unmount this row before
  // onRun() resolves — setBusy after that would warn (and is a no-op state
  // update on an unmounted component).
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);
  const active = busy || pending;
  const iconClassName = recheckIconClass(active);

  const handleClick = async () => {
    setBusy(true);
    try {
      await onRun();
    } catch {
      /* toast handled by the mutation */
    } finally {
      if (aliveRef.current) setBusy(false);
    }
  };

  return (
    <button
      type="button"
      aria-labelledby={labelId}
      title={tooltip}
      disabled={active}
      onClick={handleClick}
      className="flex size-6 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
    >
      <ArrowClockwise size={13} className={iconClassName} />
      <ScreenReaderLabel id={labelId}>{label}</ScreenReaderLabel>
    </button>
  );
}
